import React, { useEffect, useState } from "react";
import { Link as RouterLink, useNavigate, useParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  InputAdornment,
  Snackbar,
  TextField,
  Typography,
} from "@mui/material";
import {
  CheckCircle,
  LockOutlined,
  RadioButtonUnchecked,
  Visibility,
  VisibilityOff,
} from "@mui/icons-material";
import axios from "axios";
import { api } from "@api/api";
import IdentidadeVisual from "../../estilos/identidadeVisual";

const AZUL_REDEFINICAO = "#0060ef";

const campoSenhaSx = {
  "&&& .MuiInputLabel-root": {
    color: AZUL_REDEFINICAO,
    fontSize: "14px",
  },

  "&&& .MuiInputLabel-root.Mui-focused": {
    color: AZUL_REDEFINICAO,
  },

  "&&& .MuiInputLabel-root.Mui-error": {
    color: "#d32f2f",
  },

  "&&& .MuiOutlinedInput-root": {
    height: "34px",
    borderRadius: "6px",
    background: "linear-gradient(135deg, #ffffff 0%, #f3f7ff 100%)",
    color: "#182039",
    fontSize: "14px",
    px: "12px",

    "& fieldset": {
      borderColor: "#a4bfff",
      borderWidth: "1px",
    },

    "&:hover fieldset": {
      borderColor: "#7ca6fa",
    },

    "&.Mui-focused fieldset": {
      borderColor: AZUL_REDEFINICAO,
      borderWidth: "1.5px",
    },

    "&.Mui-error fieldset": {
      borderColor: "#d32f2f",
    },
  },

  "&&& .MuiOutlinedInput-root .MuiOutlinedInput-input": {
    fontSize: "13px",
    lineHeight: 1.25,
    height: "auto",
    minWidth: 0,
    p: "7px 0",

    "&::placeholder": {
      color: "#91a0b7",
      opacity: 1,
    },
  },

  "&&& .MuiFormHelperText-root": {
    fontSize: "11px",
    lineHeight: 1.3,
    mx: 0,
  },
};

const layoutRedefinicaoSx = {
  minHeight: "100vh",

  "@supports (min-height: 100dvh)": {
    minHeight: "100dvh",
  },

  boxSizing: "border-box",
  px: { xs: "12px", sm: "16px" },
  py: "8px",
};

const conteudoRedefinicaoSx = {
  width: "100%",
  maxWidth: "386px",
  mx: "auto",

  "& *, & *::before, & *::after": {
    boxSizing: "border-box",
  },

  "&& h1.MuiTypography-root": {
    fontSize: "22px",
    lineHeight: 1.2,
  },

  "&& .MuiButton-root": {
    fontSize: "14px",
    lineHeight: 1.2,
  },
};

function requisitosDaSenha(senha) {
  return [
    {
      texto: "Mínimo de 8 caracteres",
      valido: senha.length >= 8,
    },
    {
      texto: "Pelo menos 1 letra maiúscula",
      valido: /[A-Z]/.test(senha),
    },
    {
      texto: "Pelo menos 1 letra minúscula",
      valido: /[a-z]/.test(senha),
    },
    {
      texto: "Pelo menos 1 número",
      valido: /\d/.test(senha),
    },
    {
      texto: "Pelo menos 1 caractere especial (@$!%*?&)",
      valido: /[@$!%*?&]/.test(senha),
    },
  ];
}

export default function RedefinirSenha() {
  const navigate = useNavigate();
  const { token } = useParams();

  const [novaSenha, setNovaSenha] = useState("");
  const [confirmarSenha, setConfirmarSenha] = useState("");
  const [mostrarSenha, setMostrarSenha] = useState(false);
  const [mostrarConfirmacao, setMostrarConfirmacao] = useState(false);
  const [loading, setLoading] = useState(false);
  const [redefinida, setRedefinida] = useState(false);

  const [aviso, setAviso] = useState({
    aberto: false,
    mensagem: "",
    severidade: "success",
  });

  const requisitos = requisitosDaSenha(novaSenha);

  const caracteresPermitidos = /^[A-Za-z\d@$!%*?&]*$/.test(novaSenha);

  const quantidadeAtendida = requisitos.filter((item) => item.valido).length;

  const senhaForte =
    quantidadeAtendida === requisitos.length && caracteresPermitidos;

  const forca = !novaSenha
    ? {
        texto: "Não informada",
        cor: "#697689",
        segmentos: 0,
      }
    : senhaForte
      ? {
          texto: "Forte",
          cor: "#388e3c",
          segmentos: 4,
        }
      : quantidadeAtendida >= 3
        ? {
            texto: "Média",
            cor: "#ad6200",
            segmentos: 3,
          }
        : {
            texto: "Fraca",
            cor: "#d32f2f",
            segmentos: Math.max(1, Math.min(2, quantidadeAtendida)),
          };

  const bloqueado = loading || redefinida || !token;

  const senhasDiferentes =
    confirmarSenha.length > 0 && novaSenha !== confirmarSenha;

  useEffect(() => {
    if (!redefinida) return;

    const timer = setTimeout(() => {
      navigate("/login", { replace: true });
    }, 2000);

    return () => clearTimeout(timer);
  }, [redefinida, navigate]);

  const mostrarAviso = (mensagem, severidade) => {
    setAviso({
      aberto: true,
      mensagem,
      severidade,
    });
  };

  const fecharAviso = (_, motivo) => {
    if (motivo === "clickaway") return;

    setAviso((atual) => ({
      ...atual,
      aberto: false,
    }));
  };

  const adornosSenha = (visivel, alterarVisibilidade, nome) => ({
    startAdornment: (
      <InputAdornment position="start">
        <LockOutlined
          sx={{
            color: AZUL_REDEFINICAO,
            fontSize: 20,
          }}
        />
      </InputAdornment>
    ),

    endAdornment: (
      <InputAdornment position="end">
        <IconButton
          type="button"
          size="small"
          edge="end"
          disabled={bloqueado}
          aria-label={`${visivel ? "Ocultar" : "Mostrar"} ${nome}`}
          aria-pressed={visivel}
          onClick={() => alterarVisibilidade((atual) => !atual)}
          onMouseDown={(event) => event.preventDefault()}
          sx={{ color: AZUL_REDEFINICAO }}
        >
          {visivel ? (
            <VisibilityOff sx={{ fontSize: 20 }} />
          ) : (
            <Visibility sx={{ fontSize: 20 }} />
          )}
        </IconButton>
      </InputAdornment>
    ),
  });

  const handleRedefinirSenha = async (event) => {
    event.preventDefault();

    if (loading || redefinida) return;

    if (!token) {
      mostrarAviso(
        "Link incompleto. Solicite um novo link de recuperação.",
        "error",
      );
      return;
    }

    if (novaSenha !== confirmarSenha) {
      mostrarAviso("As senhas não coincidem.", "error");
      return;
    }

    const senhaValida =
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/.test(
        novaSenha,
      );

    if (!senhaValida) {
      mostrarAviso(
        "Use no mínimo 8 caracteres, com letra maiúscula, minúscula, número e símbolo (@$!%*?&). Use apenas letras de A a Z, números e esses símbolos.",
        "error",
      );
      return;
    }

    setLoading(true);

    try {
      await axios.post(api.resetPassword(), {
        token,
        novaSenha,
      });

      setRedefinida(true);

      mostrarAviso(
        "Senha redefinida com sucesso! Redirecionando para o login...",
        "success",
      );
    } catch (error) {
      const dados = error.response?.data;

      const mensagens = [dados?.msg, dados?.message]
        .flat()
        .filter((item) => typeof item === "string" && item.trim());

      mostrarAviso(
        mensagens[0] || "Não foi possível redefinir a senha. Tente novamente.",
        "error",
      );
    } finally {
      setLoading(false);
    }
  };

  const propsCampo = {
    fullWidth: true,
    required: true,
    disabled: bloqueado,
    autoComplete: "new-password",
    InputLabelProps: {
      shrink: true,
    },
    sx: campoSenhaSx,
  };

  return (
    <IdentidadeVisual variante="acesso" suave sx={layoutRedefinicaoSx}>
      <Box sx={conteudoRedefinicaoSx}>
        <Box
          sx={{
            textAlign: "center",
            color: "#ffffff",
            mb: "12px",
          }}
        >
          <Typography
            component="div"
            sx={{
              fontSize: "44px",
              fontWeight: 800,
              letterSpacing: "1px",
              lineHeight: "40px",
              textShadow: "0 1px 4px rgba(0, 70, 150, 0.08)",
            }}
          >
            SEAR
          </Typography>

          <Box
            sx={{
              width: "246px",
              maxWidth: "100%",
              height: "1px",
              backgroundColor: "rgba(255, 255, 255, 0.35)",
              mx: "auto",
              my: "4px",
            }}
          />

          <Typography
            component="div"
            sx={{
              fontSize: "13px",
              lineHeight: 1.25,
            }}
          >
            Sistema Eletrônico de Atendimento Remoto
          </Typography>
        </Box>

        <Box
          component="main"
          aria-labelledby="titulo-redefinicao"
          sx={{
            width: "100%",
            p: "13px 16px 14px",
            borderRadius: "12px",
            border: "1px solid rgba(220, 232, 250, 0.9)",
            background:
              "linear-gradient(145deg, #ffffff 0%, #fcfdff 58%, #f3f7ff 100%)",
            boxShadow: "0 10px 28px rgba(0, 55, 140, 0.13)",
          }}
        >
          <Box
            sx={{
              width: "44px",
              height: "44px",
              mx: "auto",
              mb: "6px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: "50%",
              backgroundColor: "#e7efff",
            }}
          >
            <LockOutlined
              sx={{
                color: AZUL_REDEFINICAO,
                fontSize: 24,
              }}
            />
          </Box>

          <Typography
            id="titulo-redefinicao"
            component="h1"
            sx={{
              mb: "4px",
              textAlign: "center",
              color: "#1f2c51",
              fontSize: "22px",
              fontWeight: 700,
              lineHeight: 1.2,
            }}
          >
            Redefinir senha
          </Typography>

          <Typography
            component="div"
            sx={{
              mb: "18px",
              textAlign: "center",
              color: "#6b809e",
              fontSize: "12px",
              lineHeight: 1.3,
            }}
          >
            Crie uma nova senha para sua conta.
            <br />
            Use uma senha forte e diferente da anterior.
          </Typography>

          {!token && (
            <Alert severity="error" sx={{ mb: "14px" }}>
              Link incompleto. Solicite um novo link de recuperação.
            </Alert>
          )}

          <Box
            component="form"
            onSubmit={handleRedefinirSenha}
            aria-busy={loading}
            sx={{
              display: "flex",
              flexDirection: "column",
            }}
          >
            <TextField
              {...propsCampo}
              id="redefinicao-nova-senha"
              name="novaSenha"
              label="Nova senha"
              placeholder="Digite sua nova senha"
              type={mostrarSenha ? "text" : "password"}
              value={novaSenha}
              onChange={(event) => {
                setNovaSenha(event.target.value);
              }}
              error={!caracteresPermitidos}
              helperText={
                !caracteresPermitidos
                  ? "Use apenas letras sem acento, números e os símbolos @$!%*?&."
                  : ""
              }
              inputProps={{
                "aria-describedby": caracteresPermitidos
                  ? "requisitos-redefinicao"
                  : "requisitos-redefinicao redefinicao-nova-senha-helper-text",
              }}
              InputProps={adornosSenha(
                mostrarSenha,
                setMostrarSenha,
                "nova senha",
              )}
              sx={{
                ...campoSenhaSx,
                mb: "18px",
              }}
            />

            <TextField
              {...propsCampo}
              id="redefinicao-confirmar-senha"
              name="confirmarSenha"
              label="Confirmar nova senha"
              placeholder="Digite novamente sua nova senha"
              type={mostrarConfirmacao ? "text" : "password"}
              value={confirmarSenha}
              onChange={(event) => {
                setConfirmarSenha(event.target.value);
              }}
              error={senhasDiferentes}
              helperText={senhasDiferentes ? "As senhas não coincidem." : ""}
              InputProps={adornosSenha(
                mostrarConfirmacao,
                setMostrarConfirmacao,
                "confirmação da nova senha",
              )}
              sx={{
                ...campoSenhaSx,
                mb: "10px",
              }}
            />

            <Box
              id="requisitos-redefinicao"
              sx={{
                p: "8px 10px",
                borderRadius: "8px",
                border: "1px solid #dfe8f5",
                backgroundColor: "#f2f6fd",
              }}
            >
              <Typography
                component="div"
                sx={{
                  mb: "5px",
                  color: "#46516a",
                  fontSize: "12px",
                  lineHeight: "15px",
                  fontWeight: 600,
                }}
              >
                Requisitos da senha:
              </Typography>

              <Box
                component="ul"
                sx={{
                  m: 0,
                  p: 0,
                  listStyle: "none",
                  display: "grid",
                  gap: "4px",
                }}
              >
                {requisitos.map((item) => (
                  <Box
                    component="li"
                    key={item.texto}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                      minHeight: "14px",
                      color: item.valido ? "#388e3c" : "#52627b",
                    }}
                  >
                    {item.valido ? (
                      <CheckCircle
                        sx={{
                          fontSize: 12,
                          flexShrink: 0,
                        }}
                      />
                    ) : (
                      <RadioButtonUnchecked
                        sx={{
                          fontSize: 12,
                          flexShrink: 0,
                        }}
                      />
                    )}

                    <Box
                      component="span"
                      sx={{
                        fontSize: "11px",
                        lineHeight: "14px",
                      }}
                    >
                      <Box
                        component="span"
                        sx={{
                          position: "absolute",
                          width: "1px",
                          height: "1px",
                          p: 0,
                          m: "-1px",
                          overflow: "hidden",
                          clip: "rect(0, 0, 0, 0)",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {item.valido ? "Atendido: " : "Pendente: "}
                      </Box>

                      {item.texto}
                    </Box>
                  </Box>
                ))}
              </Box>
            </Box>

            <Box
              sx={{
                mt: "10px",
                mb: "12px",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 1,
                  mb: "4px",
                }}
              >
                <Typography
                  id="rotulo-forca-redefinicao"
                  component="div"
                  sx={{
                    color: "#46516a",
                    fontSize: "12px",
                    lineHeight: "15px",
                    fontWeight: 600,
                  }}
                >
                  Força da senha:
                </Typography>

                {novaSenha && (
                  <Typography
                    component="div"
                    sx={{
                      color: forca.cor,
                      fontSize: "11px",
                      lineHeight: "15px",
                      fontWeight: 600,
                    }}
                  >
                    {forca.texto}
                  </Typography>
                )}
              </Box>

              <Box
                role="progressbar"
                aria-labelledby="rotulo-forca-redefinicao"
                aria-valuemin={0}
                aria-valuemax={4}
                aria-valuenow={forca.segmentos}
                aria-valuetext={forca.texto}
                sx={{
                  display: "grid",
                  gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
                  gap: "5px",
                }}
              >
                {Array.from({ length: 4 }, (_, indice) => (
                  <Box
                    key={indice}
                    sx={{
                      height: "8px",
                      borderRadius: "5px",
                      backgroundColor:
                        indice < forca.segmentos ? forca.cor : "#dfe7f2",
                    }}
                  />
                ))}
              </Box>
            </Box>

            <Button
              type="submit"
              variant="contained"
              disableElevation
              fullWidth
              disabled={bloqueado}
              sx={{
                height: "34px",
                minHeight: "34px",
                mb: "5px",
                py: 0,
                borderRadius: "7px",
                background: "linear-gradient(180deg, #0870ff 0%, #005cf2 100%)",
                boxShadow: "0 2px 5px rgba(0, 96, 239, 0.14)",
                color: "#ffffff",
                textTransform: "none",
                fontSize: "14px",
                fontWeight: 600,

                "&:hover": {
                  background:
                    "linear-gradient(180deg, #0065f5 0%, #0055df 100%)",
                  boxShadow: "0 2px 5px rgba(0, 96, 239, 0.18)",
                },
              }}
            >
              {loading ? (
                <CircularProgress
                  size={20}
                  color="inherit"
                  aria-label="Salvando nova senha"
                />
              ) : redefinida ? (
                "Senha redefinida"
              ) : (
                "Salvar nova senha"
              )}
            </Button>

            <Button
              component={RouterLink}
              to="/login"
              variant="outlined"
              fullWidth
              sx={{
                minHeight: 30,
                height: 30,
                py: 0,
                borderRadius: "6px",
                borderColor: "#a4bfff",
                backgroundColor: "rgba(245, 249, 255, 0.65)",
                color: AZUL_REDEFINICAO,
                textTransform: "none",
                fontSize: "14px",
                fontWeight: 600,

                "&:hover": {
                  borderColor: AZUL_REDEFINICAO,
                  backgroundColor: "#edf5ff",
                },
              }}
            >
              Voltar para o login
            </Button>
          </Box>
        </Box>
      </Box>

      <Snackbar
        open={aviso.aberto}
        autoHideDuration={6000}
        onClose={fecharAviso}
        anchorOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
      >
        <Alert
          onClose={fecharAviso}
          severity={aviso.severidade}
          sx={{ width: "100%" }}
        >
          {aviso.mensagem}
        </Alert>
      </Snackbar>
    </IdentidadeVisual>
  );
}
